import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { bmsImportCount, useBmsImport } from '../../../api/bms/bms';
import { BmsImportCount200 } from '../../../api/model';
import { useOptionsForBinary } from '../../../hooks/api';
import { getAxiosRequestConfig } from '../../../utils/api';
import yup from '../../../utils/custom-yup';
import { executeDownload } from '../../../utils/download';
import FileDrop from '../../atoms/FileDrop';
import BlobErrorModal from '../../modules/BlobErrorModal';
import Modal from '../../modules/Modal';
import OkModal from '../../modules/OkModal';
import SpinnerModal from '../../modules/SpinnerModal';

interface Props {
  active: boolean;
  onClose: () => void;
}

/**
 * ベース原稿タブのインポートダイアログ
 * @param param0
 * @returns
 */
function BmsImportModal({ active, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    importFiles: yup.array().required(),
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  // インポート処理定義

  const optionsForBinary = useOptionsForBinary(language);
  const { mutateAsync: BmsAsync, error: BmsImportError } = useBmsImport<AxiosError<Blob>>(optionsForBinary);

  const [displayBmsImportError, setDisplayBmsImportError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayBmsImportOk, setDisplayBmsImportOk] = useState(false);
  const [importMsg, setImportMsg] = useState<string | undefined>('');

  useEffect(() => {
    setDisplayBmsImportError(BmsImportError != null);
    setIsSubmitting(false);
  }, [BmsImportError]);

  // キャンセル
  const onCancelClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // インポート実行
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      void handleSubmit(async (data: FieldValues) => {
        if (data.importFiles.length > 0) {
          let filename: string | undefined = '';

          setIsSubmitting(true);
          await BmsAsync(
            { data: { file: data.importFiles?.[0] } },
            {
              // API アクセスが成功したら、ダウンロードを実行
              onSuccess: (response) => {
                executeDownload(response);

                const disposition = response.headers['content-disposition'];
                const type = response.headers['content-type'];
                if (disposition != null && type != null) {
                  const filenameRegex1 = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                  const matches1 = filenameRegex1.exec(disposition);
                  if (matches1 != null && matches1[1] != null) {
                    filename = decodeURI(matches1[1].replace(/['"]/g, ''));
                  }
                  const filenameRegex2 = /filename\*=utf-8''((['"]).*?\2|[^;\n]*)/;
                  const matches2 = filenameRegex2.exec(disposition);
                  if (matches2 != null && matches2[1] != null) {
                    filename = decodeURI(matches2[1].replace(/['"]/g, ''));
                  }
                }
              },
            }
          );

          if (filename != null) {
            const words = filename.split('_');
            const id = words[1].replace('.log', '');
            const axiosRequestConfig = getAxiosRequestConfig(language);

            await bmsImportCount(Number(id), axiosRequestConfig).then(
              (responseCount: AxiosResponse<BmsImportCount200>) => {
                const { data: dataCount } = responseCount;
                setImportMsg(`${Number(dataCount.import_count)}件のBMSデータをインポートしました。`);
                setDisplayBmsImportOk(true);
              }
            );
          }
        }
      })();
    },
    [setIsSubmitting, handleSubmit, BmsAsync, setDisplayBmsImportOk, language]
  );

  return (
    <Modal active={active} additionalClassName="mtsp" displayCloseButton onClose={onCancelClicked}>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="formarea1">
            <label htmlFor="">{t('添付ファイル')}</label>
            <div>
              <FileDrop id="importFiles" name="importFiles" setValue={setValue} />
              {errors.importFiles?.message != null && (
                <p className="form-error">{errors.importFiles.message.toString()}</p>
              )}
            </div>
          </div>
          <div className="btnfr btnfr2 mb0">
            <div>
              <button type="button" className="text-button" onClick={() => onCancelClicked()}>
                <span className="btn2 mr10 btnbg-g">
                  <span className="modal-close2">{t('キャンセル')}</span>
                </span>
              </button>
              <span>
                <input type="submit" id="" name="" value={t('インポート').toString()} className="btn1" />
              </span>{' '}
            </div>
          </div>
        </fieldset>
      </form>
      {isSubmitting && <SpinnerModal />}
      {displayBmsImportError && (
        <BlobErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayBmsImportError(false);
          }}
          error={BmsImportError}
        />
      )}
      {displayBmsImportOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayBmsImportOk(false);
            reset();
            onClose();
          }}
        >
          <div>{t(importMsg ?? '')}</div>
        </OkModal>
      )}
    </Modal>
  );
}

export default BmsImportModal;
